import React, { useState, useEffect, useRef } from 'react';
import { ROW_HEIGHT } from './constants';

type WorkSchedule = {
  id: string | number;
  psychologistId: number | string;
  weekday: number;
  startTime: string;
  endTime: string;
  active: boolean;
  scheduleType?: 'standard' | 'reschedule_only';
};

function timeToMinutes(hhmm: string): number {
  const [hh, mm] = hhmm.split(':').map(Number);
  return hh * 60 + mm;
}

type Props = {
  template: WorkSchedule[];
  visibleTimeSlots: string[];
  gridRef: React.RefObject<HTMLDivElement>;
  selectedRangeId: string | number | null;
  onSelectedChange: (id: string | number | null) => void;
  onEdit?: (schedule: WorkSchedule) => void;
  onDelete?: (scheduleId: string | number) => Promise<void>;
  isSmallScreen?: boolean;
  deletedScheduleIds?: Set<string | number>;
  onRestoreSchedule?: (scheduleId: string | number) => void;
};

const WorkRangeOverlay: React.FC<Props> = ({ template, visibleTimeSlots, gridRef, selectedRangeId, onEdit, isSmallScreen = false, deletedScheduleIds = new Set(), onRestoreSchedule }) => {

  const remToPx = (rem: number): number => {
    const fontSize = parseFloat(getComputedStyle(document.documentElement).fontSize);
    return rem * fontSize;
  };

  const ROW_HEIGHT_PX = remToPx(ROW_HEIGHT);

  const [hoveredId, setHoveredId] = useState<string | number | null>(null);
  const [cellPositions, setCellPositions] = useState<Map<number, { left: number; width: number }>>(new Map());
  const [gridMetrics, setGridMetrics] = useState<{ headerHeight: number; firstSlotMin: number; lastSlotMin: number } | null>(null);
  const visibleRangeIdsRef = useRef<Set<string | number>>(new Set());
  const [fadingInRangeIds, setFadingInRangeIds] = useState<Set<string | number>>(new Set());
  const loggedDeletedSchedulesRef = useRef<Set<string | number>>(new Set());
  
  useEffect(() => {
    if (!visibleTimeSlots.length || !gridRef.current) return;

    let timeoutId: NodeJS.Timeout;

    const updatePositions = (attempt = 0) => {
      if (!gridRef.current || !visibleTimeSlots.length) return;

      const rect = gridRef.current.getBoundingClientRect();
      const cellsByDay = new Map<number, { left: number; width: number }>();

      const firstTimeSlot = visibleTimeSlots[0];

      const allGridChildren = Array.from(gridRef.current.children);
      const firstDataCell = allGridChildren.find(el => {
        const htmlEl = el as HTMLElement;
        return htmlEl.hasAttribute('data-grid-cell');
      }) as HTMLElement | null;

      // Sumamos scrollTop: si el grid está scrolleado, la primera celda queda
      // por encima del viewport y su top relativo es negativo. Sin esto, la
      // condición headerHeight >= 0 fallaba y las posiciones nunca se calculaban
      // mientras el grid no estuviera scrolleado hasta arriba del todo.
      const calculatedHeaderHeight = firstDataCell
        ? firstDataCell.getBoundingClientRect().top - rect.top + gridRef.current.scrollTop
        : 0;

      // Cantidad real de columnas (días) presentes en el grid. En vista mobile
      // son 3-4, en desktop 7: no hay que asumir un número fijo.
      const presentDays = new Set<string>();
      gridRef.current.querySelectorAll('[data-grid-cell][data-day-index]').forEach(el => {
        const di = el.getAttribute('data-day-index');
        if (di !== null) presentDays.add(di);
      });
      const expectedCols = presentDays.size;

      for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
        const cellSelector = `[data-grid-cell][data-day-index="${dayIndex}"][data-time-slot="${firstTimeSlot}"]`;
        let dayCell = gridRef.current.querySelector(cellSelector) as HTMLElement | null;

        if (!dayCell) {
          const fallbackSelector = `[data-grid-cell][data-day-index="${dayIndex}"]`;
          dayCell = gridRef.current.querySelector(fallbackSelector) as HTMLElement | null;
        }

        if (dayCell) {
          const cellRect = dayCell.getBoundingClientRect();
          cellsByDay.set(dayIndex, {
            left: cellRect.left - rect.left,
            width: cellRect.width
          });
        }
      }

      if (expectedCols > 0 && cellsByDay.size >= expectedCols && calculatedHeaderHeight >= 0) {
        setCellPositions(cellsByDay);
        setGridMetrics({
          headerHeight: calculatedHeaderHeight,
          firstSlotMin: timeToMinutes(visibleTimeSlots[0]),
          lastSlotMin: timeToMinutes(visibleTimeSlots[visibleTimeSlots.length - 1]) + 60
        });
      } else if (attempt < 40) {
        // Reintentar aunque todavía no haya celdas (grid montándose). Antes solo
        // se reintentaba con size > 0, por lo que si el primer intento corría
        // antes de montar las celdas, las posiciones nunca se calculaban.
        timeoutId = setTimeout(() => updatePositions(attempt + 1), 50);
      }
    };

    // Throttle updates to prevent excessive re-renders
    const throttledUpdatePositions = () => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(updatePositions, 16); // ~60fps
    };

    const observer: ResizeObserver = new ResizeObserver(throttledUpdatePositions);
    const mutationObserver: MutationObserver = new MutationObserver((mutations) => {
      const hasRelevantChanges = mutations.some(mutation => {
        return Array.from(mutation.addedNodes).some(node => {
          if (node.nodeType === 1) {
            const el = node as HTMLElement;
            return el.hasAttribute('data-grid-cell') || el.querySelector('[data-grid-cell]');
          }
          return false;
        });
      });
      if (hasRelevantChanges) {
        throttledUpdatePositions();
      }
    });

    updatePositions();
    
    if (gridRef.current) {
      observer.observe(gridRef.current);
      mutationObserver.observe(gridRef.current, { childList: true, subtree: true });
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      if (observer) observer.disconnect();
      if (mutationObserver) mutationObserver.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleTimeSlots]);
  
  useEffect(() => {
    if (!gridMetrics || cellPositions.size === 0) return;
    
    const { firstSlotMin, lastSlotMin } = gridMetrics;
    const crossesMidnight = lastSlotMin < firstSlotMin;
    
    const currentVisibleIds = new Set<string | number>();
    
    // Use requestAnimationFrame to defer expensive calculations
    const updateVisibility = () => {
      template.filter(t => t.active).forEach(sched => {
        const schedStart = timeToMinutes(sched.startTime);
        let schedEnd = timeToMinutes(sched.endTime);
        
        if (schedEnd === 0 && sched.endTime === '00:00') {
          schedEnd = 1440;
        }
        
        const wraps = schedEnd <= schedStart;
        
        if (wraps) return;
        
        let isVisible = false;
        if (crossesMidnight) {
          isVisible = schedStart < lastSlotMin || schedEnd > firstSlotMin;
        } else {
          isVisible = schedEnd > firstSlotMin && schedStart < lastSlotMin;
        }
        
        if (isVisible) {
          const visibleStart = Math.max(schedStart, firstSlotMin);
          const visibleEnd = Math.min(schedEnd, lastSlotMin);
          const visibleDuration = visibleEnd - visibleStart;
          const totalDuration = schedEnd - schedStart;
          const MIN_VISIBLE_MINUTES = 30;
          const isOneHourRange = totalDuration === 60;
          const isClippedTop = schedStart < firstSlotMin;
          const isClippedBottom = schedEnd > lastSlotMin;
          const isClipped = isClippedTop || isClippedBottom;
          
          if (visibleDuration >= MIN_VISIBLE_MINUTES || (isOneHourRange && !isClipped)) {
            currentVisibleIds.add(sched.id);
          }
        }
      });
      
      const previousIds = visibleRangeIdsRef.current;
      const newRanges = Array.from(currentVisibleIds).filter(id => !previousIds.has(id));
      
      if (newRanges.length > 0) {
        setFadingInRangeIds(new Set(newRanges));
        setTimeout(() => {
          setFadingInRangeIds(new Set());
        }, 300);
      }
      
      visibleRangeIdsRef.current = currentVisibleIds;
    };
    
    // Defer the calculation to the next frame
    requestAnimationFrame(updateVisibility);
  }, [gridMetrics, cellPositions, template]);
  
  if (!visibleTimeSlots.length || !gridRef.current || !gridMetrics || cellPositions.size === 0) {
    return null;
  }

  const { headerHeight, firstSlotMin, lastSlotMin } = gridMetrics;

  const rangesByDay: Map<number, WorkSchedule[]> = new Map();
  template.filter(t => t.active).forEach(t => {
    const existing = rangesByDay.get(t.weekday) || [];
    rangesByDay.set(t.weekday, [...existing, t]);
  });

  const selectedSchedule = template.find(t => t.id === selectedRangeId);
  let selectedTopLine: number | null = null;
  let selectedBottomLine: number | null = null;

  if (selectedSchedule) {
    const schedStart = timeToMinutes(selectedSchedule.startTime);
    let schedEnd = timeToMinutes(selectedSchedule.endTime);
    
    if (schedEnd === 0 && selectedSchedule.endTime === '00:00') {
      schedEnd = 1440;
    }
    
    const wraps = schedEnd <= schedStart;
    const crossesMidnight = lastSlotMin < firstSlotMin;
    
    let isVisible = false;
    if (!wraps) {
      if (crossesMidnight) {
        isVisible = schedStart < lastSlotMin || schedEnd > firstSlotMin;
      } else {
        isVisible = schedEnd > firstSlotMin && schedStart < lastSlotMin;
      }
    }
    
    if (isVisible) {
      let visibleStart, visibleEnd, offsetFromFirstStart, offsetFromFirstEnd;
      
      if (crossesMidnight) {
        if (schedStart >= firstSlotMin) {
          visibleStart = schedStart;
          visibleEnd = Math.min(schedEnd, 24 * 60);
          offsetFromFirstStart = visibleStart - firstSlotMin;
          offsetFromFirstEnd = visibleEnd - firstSlotMin;
        } else if (schedEnd <= lastSlotMin) {
          visibleStart = Math.max(schedStart, 0);
          visibleEnd = Math.min(schedEnd, lastSlotMin);
          offsetFromFirstStart = (24 * 60 - firstSlotMin) + visibleStart;
          offsetFromFirstEnd = (24 * 60 - firstSlotMin) + visibleEnd;
        } else {
          visibleStart = Math.max(schedStart, 0);
          visibleEnd = lastSlotMin;
          offsetFromFirstStart = (24 * 60 - firstSlotMin) + visibleStart;
          offsetFromFirstEnd = (24 * 60 - firstSlotMin) + visibleEnd;
        }
      } else {
        visibleStart = Math.max(schedStart, firstSlotMin);
        visibleEnd = Math.min(schedEnd, lastSlotMin);
        offsetFromFirstStart = visibleStart - firstSlotMin;
        offsetFromFirstEnd = visibleEnd - firstSlotMin;
      }
      
      const visibleDuration = visibleEnd - visibleStart;
      const totalDuration = schedEnd - schedStart;
      const MIN_VISIBLE_MINUTES = 30;
      const isOneHourRange = totalDuration === 60;
      
      const isClippedTop = schedStart < firstSlotMin;
      const isClippedBottom = schedEnd > lastSlotMin;
      const isClipped = isClippedTop || isClippedBottom;
      
      if (visibleDuration >= MIN_VISIBLE_MINUTES || (isOneHourRange && !isClipped)) {
        selectedTopLine = headerHeight + (offsetFromFirstStart / 60) * ROW_HEIGHT_PX;
        selectedBottomLine = headerHeight + (offsetFromFirstEnd / 60) * ROW_HEIGHT_PX;
      }
    }
  }

  return (
    <>
      {selectedTopLine !== null && !isSmallScreen && (
        <div
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: selectedTopLine,
            height: 1,
            background: '#60a5fa',
            pointerEvents: 'none',
            zIndex: 18,
            animation: 'fadeIn 0.35s ease-out'
          }}
        />
      )}
      {selectedBottomLine !== null && !isSmallScreen && (
        <div
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: selectedBottomLine,
            height: 1,
            background: '#6D57BC',
            pointerEvents: 'none',
            zIndex: 18,
            animation: 'fadeIn 0.35s ease-out'
          }}
        />
      )}
      {Array.from(rangesByDay.entries()).map(([weekday, schedules]) => {
        return schedules.map(sched => {
          const schedStart = timeToMinutes(sched.startTime);
          let schedEnd = timeToMinutes(sched.endTime);
          
          if (schedEnd === 0 && sched.endTime === '00:00') {
            schedEnd = 1440;
          }
          
          const wraps = schedEnd <= schedStart;
          
          if (wraps) return null;
          
          const crossesMidnight = lastSlotMin < firstSlotMin;
          
          let isVisible = false;
          if (crossesMidnight) {
            isVisible = schedStart < lastSlotMin || schedEnd > firstSlotMin;
          } else {
            isVisible = schedEnd > firstSlotMin && schedStart < lastSlotMin;
          }
          
          if (!isVisible) return null;
          
          let visibleStart, visibleEnd, offsetFromFirst;
          
          if (crossesMidnight) {
            if (schedStart >= firstSlotMin) {
              visibleStart = schedStart;
              visibleEnd = Math.min(schedEnd, 24 * 60);
              offsetFromFirst = visibleStart - firstSlotMin;
            } else if (schedEnd <= lastSlotMin) {
              visibleStart = Math.max(schedStart, 0);
              visibleEnd = Math.min(schedEnd, lastSlotMin);
              offsetFromFirst = (24 * 60 - firstSlotMin) + visibleStart;
            } else {
              visibleStart = Math.max(schedStart, 0);
              visibleEnd = lastSlotMin;
              offsetFromFirst = (24 * 60 - firstSlotMin) + visibleStart;
            }
          } else {
            visibleStart = Math.max(schedStart, firstSlotMin);
            visibleEnd = Math.min(schedEnd, lastSlotMin);
            offsetFromFirst = visibleStart - firstSlotMin;
          }
          
          const visibleDuration = visibleEnd - visibleStart;
          
          if (visibleDuration <= 0) return null;
          
          const cellPos = cellPositions.get(weekday);
          if (!cellPos) return null;
          
          const isClippedTop = schedStart < firstSlotMin;
          const isClippedBottom = schedEnd > lastSlotMin;
          const isClipped = isClippedTop || isClippedBottom;
          
          const totalDuration = schedEnd - schedStart;
          const MIN_VISIBLE_MINUTES = 30;
          const isOneHourRange = totalDuration === 60;
          
          if (visibleDuration < MIN_VISIBLE_MINUTES && !(isOneHourRange && !isClipped)) {
            return null;
          }
          
          let topPx = headerHeight + (offsetFromFirst / 60) * ROW_HEIGHT_PX;
          let heightPx = (visibleDuration / 60) * ROW_HEIGHT_PX;
          
          const MIN_BUBBLE_HEIGHT = 48;
          const originalHeightPx = heightPx;
          
          if (heightPx < MIN_BUBBLE_HEIGHT) {
            if (isClippedBottom && !isClippedTop) {
              heightPx = Math.min(MIN_BUBBLE_HEIGHT, originalHeightPx + 20);
            } else if (isClippedTop && !isClippedBottom) {
              const adjustment = Math.min(MIN_BUBBLE_HEIGHT - heightPx, topPx - headerHeight);
              topPx = topPx - adjustment;
              heightPx = heightPx + adjustment;
            } else if (!isClipped) {
              heightPx = Math.max(MIN_BUBBLE_HEIGHT, heightPx);
            }
          }
          
          const isHovered = hoveredId === sched.id;
          const isBeingDeleted = deletedScheduleIds.has(sched.id);
          const isRescheduleOnly = sched.scheduleType === 'reschedule_only';

          // Debug: log deleted schedules
          if (isBeingDeleted && !loggedDeletedSchedulesRef.current.has(sched.id)) {
            loggedDeletedSchedulesRef.current.add(sched.id);
          }

          const isFadingIn = fadingInRangeIds.has(sched.id);
          const animation = isFadingIn ? 'fadeIn 0.3s ease-out' : 'none';

          return (
            <div
              key={`work-range-overlay-${sched.id}`}
              data-work-range-bubble="true"
              onMouseEnter={() => setHoveredId(sched.id)}
              onMouseLeave={() => setHoveredId(null)}
              onClick={(e) => {
                e.stopPropagation();
                if (!isBeingDeleted && onEdit) onEdit(sched);
              }}
              style={{
                position: 'absolute',
                top: topPx + 4,
                left: cellPos.left + 4,
                width: cellPos.width - 8,
                height: Math.max(heightPx - 8, MIN_BUBBLE_HEIGHT - 8),
                zIndex: isHovered ? 25 : 15,
                pointerEvents: isBeingDeleted ? 'none' : 'auto',
                background: isBeingDeleted
                  ? 'repeating-linear-gradient(135deg, rgba(180,180,180,0.35), rgba(180,180,180,0.35) 4px, rgba(240,240,240,0.5) 4px, rgba(240,240,240,0.5) 8px)'
                  : isRescheduleOnly
                    ? (isHovered ? 'rgba(245, 158, 11, 0.15)' : 'rgba(245, 158, 11, 0.08)')
                    : (isHovered ? 'rgba(139, 92, 246, 0.15)' : 'rgba(139, 92, 246, 0.08)'),
                border: isBeingDeleted
                  ? '1px dashed #b0b0b0'
                  : isRescheduleOnly
                    ? (isHovered ? '1.5px dashed #f59e0b' : '1px dashed #fcd34d')
                    : (isHovered ? '1.5px dashed #a78bfa' : '1px dashed #c4b5fd'),
                borderRadius: 10,
                padding: isBeingDeleted ? '4px 8px' : '8px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                gap: isBeingDeleted ? '2px' : '0',
                color: isBeingDeleted ? '#888' : isRescheduleOnly ? '#b45309' : '#6D57BC',
                fontSize: '0.875rem',
                fontWeight: 700,
                cursor: isBeingDeleted ? 'default' : 'pointer',
                boxShadow: 'none',
                animation,
                overflow: 'hidden',
                opacity: isBeingDeleted ? 0.75 : 1,
              }}
            >
              <div style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                width: '100%',
                textAlign: 'center',
                textDecoration: isBeingDeleted ? 'line-through' : 'none',
                lineHeight: isBeingDeleted ? '1.2' : undefined,
                fontSize: isBeingDeleted ? '0.8rem' : undefined
              }}>
                {sched.startTime} - {sched.endTime}
              </div>
              {isRescheduleOnly && !isBeingDeleted && visibleDuration > 60 && (
                <div className="text-[0.6rem] font-bold uppercase leading-none text-amber-700">
                  Reprogramación
                </div>
              )}
              {isBeingDeleted && onRestoreSchedule && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onRestoreSchedule(sched.id);
                  }}
                  title="Restaurar este horario"
                  style={{
                    padding: '2px 8px',
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    background: '#7c3aed',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    lineHeight: '1.4',
                    flexShrink: 0,
                    pointerEvents: 'auto',
                  }}
                >
                  ↶ Deshacer
                </button>
              )}
            </div>
          );
        });
      })}
    </>
  );
};

export default WorkRangeOverlay;

